// useCommentTemplates
// -------------------
// DB-backed comment templates used by the Approve / Decline UI and the
// admin Templates editor.
//
// `opts.includeDeactivated` defaults to false (shelter/responder use) — the
// admin (TemplatesAdmin) passes true so it can see + reactivate soft-deleted
// rows.
//
// On first load (DB empty) the seed in TEMPLATE_SEED is inserted. If new
// seed entries are added later (ids missing from DB), they are inserted on
// the next load.
//
// Extracted from RequestWizard.jsx (May 2026) — was originally in-file there
// so the wizard could use it, but admin + responder + dashboard need it too.

import React from "react";
import { insertCommentTemplates, listCommentTemplates } from "../data/commentTemplates";

// Seed data -- used only to populate the DB on first load if empty
export const TEMPLATE_SEED = [
  {
    id: "ns1",
    category: "_NextSteps",
    title: "Next Steps — Standard",
    text: "NEXT STEPS\n\nPlease arrange a tax-compliant invoice to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices can still be uploaded or emailed quoting the reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "ns2",
    category: "_NextSteps",
    title: "Next Steps — With Payment Options",
    text: "NEXT STEPS\n\nPlease arrange a tax-compliant invoice to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nPAYMENT OPTIONS:\nZephyr no longer processes credit card payments. If your organisation can purchase the items, we'll reimburse within 1-2 business days. If not, please let us know promptly.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices accepted — upload or email quoting reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "ns3",
    category: "_NextSteps",
    title: "Approved Request Requiring Reimbursement",
    text: "Your organisation will cover costs and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\nPayment processed within 1-2 business days of receiving all documents.\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices accepted — upload or email invoicing@zephyreducation.com.au quoting reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "f01",
    category: "Fees",
    title: "Camp Fees — More Information Needed",
    text: "Thanks for your request.\n\nFor the Camp Fees, could you please provide a brochure showing camp dates and when deposit/balance payments are due? Once received, we can assess further.\n\nKind regards",
  },
  {
    id: "f02",
    category: "Fees",
    title: "Resource Fees — No Supporting Letter from School Uploaded (QLD Only)",
    text: "Thanks for your request for Resource Fees.\n\nYOUR ACTION:\n• Please ask the school for a brief letter confirming they’re unable to waive fees\n• Upload the letter via the FILES & INVOICES tab\n\nOnce received, we’ll assess the request. Kind regards",
  },
  {
    id: "f03",
    category: "Fees",
    title: "Resource Fees — Pro Rata Partial Payment",
    text: "Further to your request for assistance with Resource Fees, we have looked at the information provided and note:\n\n[EDIT AS REQUIRED — example below]\n• The brochure/statement shows an annual SRS of $200 per child.\n• Zephyr’s policy is that it does not fund retrospectively.\n• We are now half way through the school year.\n\nOn this basis, Zephyr will support Resource Fees totalling $[AMOUNT] for this family (i.e. [NUMBER] x $[AMOUNT]). Reference number is as per the Zephyr Reference No. at the top of this request.",
    appends_next_steps: "ns1",
  },
  {
    id: "f04",
    category: "Fees",
    title: "Textbooks — Reimbursement",
    text: "Thank you for your request. The Textbook reimbursement has been approved to a value of $[AMOUNT]. Reference number is as per the Zephyr Reference No. at the top of this request.",
    appends_next_steps: "ns3",
  },
  {
    id: "f05",
    category: "Fees",
    title: "Device Hire — Pro Rata when Approved",
    text: "Zephyr is happy to support the request for device hire. Our policy is to fund this on a term-by-term basis, so we’ll just need an invoice for the current term’s device hire.",
    appends_next_steps: "ns1",
  },
  {
    id: "f06",
    category: "Fees",
    title: "Fees when Approved",
    text: "Thanks for your request. Zephyr is happy to support [INSERT description of fees, e.g. Coomera Cutters Registration Fee OR Camp Fees OR Resource Fees, etc.] for $[AMOUNT]. Reference number is as per the Zephyr Reference No. at the top of this request.",
    appends_next_steps: "ns1",
  },
  {
    id: "f07",
    category: "Fees",
    title: "Stationery QLD — Approved",
    text: "Thank you for this request. This stationery pack request has been forwarded to our fulfilment team for dispatch to your attention. You should receive the items in the coming days. Reference number is as per the Zephyr Reference No. at the top of this request.\n\nKind regards",
  },
  {
    id: "f08",
    category: "Fees",
    title: "Stationery Other States — Request Not Clear",
    text: "Thanks for your request.\n\nYOUR ACTIONS:\n• Tick the items being requested and include a total amount\n• Remove any voluntary contribution fees (not supported by Zephyr)\n• Upload the revised list via FILES & INVOICES tab\n\nOnce received, we’ll review. Kind regards",
  },
  {
    id: "f09",
    category: "Fees",
    title: "Stationery Other States — Approved",
    text: "Thank you for your request which has been approved to a value of $[AMOUNT]. Please use the Zephyr Reference Number above as your invoice reference.\n\nPlease note that if your stationery list included voluntary contribution fees, these are not supported by Zephyr and are not included in the total approval.",
    appends_next_steps: "ns2",
  },
  {
    id: "u01",
    category: "Uniforms",
    title: "Uniforms — Adjusting Quantities",
    text: "PLEASE NOTE that we have adjusted the number of items requested to fall in line with Zephyr’s policy of 3 tops + 3 bottoms (but if dresses requested, reduce the top-bottom combination by the number of dresses). On this basis, the new approved amount is $[AMOUNT].",
  },
  {
    id: "u02",
    category: "Uniforms",
    title: "Uniforms — School Locker Form Submitted but Should Have Been Other",
    text: "Thanks for your request. It looks like School Locker doesn’t supply these uniforms.\n\nYOUR ACTION:\n• Start a new request and complete the standard Uniforms section\n\nWe’ll close this request. Kind regards",
  },
  {
    id: "u03",
    category: "Uniforms",
    title: "Uniforms — School Locker Should Have Been Chosen",
    text: "Thanks for your request. It looks like School Locker supplies these uniforms.\n\nYOUR ACTION:\n• Start a new request and complete the School Locker Uniforms section\n\nWe’ll close this request. Kind regards",
  },
  {
    id: "u04",
    category: "Uniforms",
    title: "Uniforms — School Locker: No SKUs",
    text: "Thanks for your School Locker uniform request.\n\nThe correct SKUs haven’t been included. To find them, click on each item on the School Locker website.\n\nYOUR ACTION:\n• List the SKUs in the Comments section of this request (one per item)\n\nOnce received, we’ll process your request. Kind regards",
  },
  {
    id: "u05",
    category: "Uniforms",
    title: "Uniforms — School Locker: Approved",
    text: "Approved for uniforms up to $[AMOUNT]. The approved form has been sent to School Locker, who will contact you to arrange collection.\n\nYOUR ACTION:\n• Print the order using the Print Order button next to the uniform items\n• Give printout to your client to take to School Locker for pickup\n\nSchool Locker will invoice Zephyr directly. Reference: OSD-[NUMBER]. Kind regards",
  },
  {
    id: "u06",
    category: "Uniforms",
    title: "Uniforms — Approved (All except School Locker and Special Suppliers)",
    text: "Thank you for your request. The uniform items requested have been approved to a value of $[AMOUNT]. The Zephyr Reference No. at the top of this request is your reference number.",
    appends_next_steps: "ns2",
  },
  {
    id: "u07",
    category: "Uniforms",
    title: "Uniforms — Special Arrangement Supplier Approved",
    text: "Thank you for your request. The uniform items requested have been approved to a value of $[AMOUNT]. The Zephyr Reference No. at the top of this request is your reference number.\n\nWe have uploaded an Authorisation for Purchase of Uniforms to the Files and Invoices tab of this request. Please download it, complete Section 2, and present it to [SUPPLIER] when collecting the uniforms. The supplier will invoice Zephyr directly — you do not need to arrange payment.\n\nIMPORTANT: The authorisation has an expiry date — please arrange collection before this date.",
  },
  {
    id: "s01",
    category: "Shoes",
    title: "Shoes — Approved",
    text: "Thank you for your request. The shoe items requested have been approved.\n\nReference number is as per the Zephyr Reference No. at the top of this request.",
    appends_next_steps: "ns3",
  },
  {
    id: "l01",
    category: "Device",
    title: "Device — Approved",
    text: "Thank you for your request. We are happy to support this request, and it has been forwarded to our fulfilment team for dispatch, to your attention. You should receive the device in the coming days. Reference number is as per the Zephyr Reference No. at the top of this request.\n\nKind regards",
  },
  {
    id: "l02",
    category: "Device",
    title: "Device — BYOD Policy Required",
    text: "Thanks for your request. We couldn’t find evidence on the school’s website that this device is a requirement.\n\nYOUR ACTION:\n• Obtain a letter from the school confirming the device is required for the child’s education\n• Upload via FILES & INVOICES tab\n\nThis request will remain open for 30 days. Kind regards",
  },
  {
    id: "l03",
    category: "Device",
    title: "Device — If Hire Option is More Economic",
    text: "Thanks for your request, however after checking the school’s website, it appears their hire program is a more economical approach.\n\n[EDIT AS REQUIRED — insert relevant device hire details and adapt as necessary]",
    appends_next_steps: "ns1",
  },
  {
    id: "l04",
    category: "Device",
    title: "Device — TAFE: More Information Needed",
    text: "Thanks for your request. Could you please provide:\n\n• Course duration (years/months)?\n• Course start date?\n• Proof of enrolment (upload via FILES & INVOICES tab)\n\nThis request will remain open for 30 days. Kind regards",
  },
  {
    id: "l05",
    category: "Device",
    title: "Device — Not Approved",
    text: "Thank you for your request.\n\nOn this occasion we are unable to approve the application, as it falls outside of Zephyr’s current guidelines and priority areas.\n\nThank you for your understanding.\n\nKind regards",
  },
  {
    id: "g01",
    category: "General",
    title: "Approved Request Requiring Reimbursement",
    text: "Thanks for your request. The amount of $[AMOUNT] is approved.\n\nYour organisation will cover costs and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\nPayment processed within 1-2 business days of receiving all documents.\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices accepted — upload or email invoicing@zephyreducation.com.au quoting reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "g02",
    category: "General",
    title: "Approved Request — Next Steps",
    text: "NEXT STEPS\n\nPlease arrange a tax-compliant invoice to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices can still be uploaded or emailed quoting the reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "g03",
    category: "General",
    title: "Approved Request — Next Steps with Payment Options",
    text: "NEXT STEPS\n\nPlease arrange a tax-compliant invoice to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nPAYMENT OPTIONS:\nZephyr no longer processes credit card payments. If your organisation can purchase the items, we'll reimburse within 1-2 business days. If not, please let us know promptly.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nIMPORTANT:\n• Submit within 2 weeks of approval\n• Requests cancelled after 60 days with no invoice\n• Late invoices accepted — upload or email quoting reference number\n\nQuestions? Please get in touch. Kind regards",
  },
  {
    id: "g04",
    category: "General",
    title: "Partial Approval",
    text: "Thank you for your request. We have reviewed the items submitted and are pleased to advise the following:\n\nAPPROVED: [list approved items here]\n\nWe are still working through the following and will be in touch shortly:\nAWAITING FURTHER INFORMATION: [list items on hold here]\n\n[DELETE IF NOT APPLICABLE]\nDECLINED: [list declined items and brief reason here]\n\nReference number is as per the Zephyr Reference No. at the top of this request.\n\nKind regards",
  },
  {
    id: "p01",
    category: "Pickers",
    title: "Order Confirmation",
    // Tokens: {{item_type}} = "Stationery"/"Device", {{ref_no}}, {{dfv_service}}.
    text: "Your {{item_type}} has been ordered .....",
  },
  {
    id: "i01",
    category: "Invoicing",
    title: "Invoice Received Before Approval",
    text: "Thank you for submitting your request and uploading the details. We noticed that the uniforms were purchased before approval was given.\n\nJust a quick reminder that, as part of Zephyr’s policy, we’re only able to reimburse items that have been approved in advance.\n\nOur approval process is designed to be quick and easy — most requests are turned around within a few hours, and always within one business day.\n\nUnfortunately, we’re unable to offer a reimbursement this time, but please let us know if you have any questions or if we can help with anything else going forward.\n\nThank you for your understanding!\n\nKind regards",
  },
  {
    id: "i02",
    category: "Invoicing",
    title: "Supplier Invoice Uploaded (or Emailed)",
    text: "Thanks for submitting the invoice which has now been sent through to our accounts team for processing. Payment should be received within the next 1-2 business days.\n\nKind regards",
  },
  {
    id: "i03",
    category: "Invoicing",
    title: "Invoice Direct from Supplier",
    text: "We have now received the invoice from the supplier for this request. It has been processed through to our accounts team for processing. Payment should be received within the next 1-2 business days.\n\nKind regards",
  },
  {
    id: "i04",
    category: "Invoicing",
    title: "Invoice from Lowes",
    text: "We are in receipt of the invoice from Lowes for this request and have now completed it.\n\nKind regards",
  },
  {
    id: "i05",
    category: "Invoicing",
    title: "Non-Compliant Invoice",
    text: "Thanks for uploading the invoice. However, it doesn’t include the school’s ABN, which is an ATO requirement for tax compliance.\n\nYOUR ACTION:\n• Ask the school to amend the invoice to include their ABN\n• Upload the corrected invoice here\n\nKind regards",
  },
  {
    id: "i06",
    category: "Invoicing",
    title: "Invoice Received for Only Part of Request",
    text: "Thank you for submitting the invoice for [INSERT items/school]. It has been processed through to our accounts team for processing. Payment should be received within the next 1-2 business days.\n\nWe look forward to receiving the invoice for [INSERT outstanding items] at your earliest convenience so that we can close out this request.\n\nKind regards",
  },
  {
    id: "i07",
    category: "Invoicing",
    title: "Confirming Reimbursement Process",
    text: "We understand that your organisation will be covering the cost of these supplies and will seek reimbursement from Zephyr. To process your reimbursement, please provide the following documentation:\n\n• A tax-compliant invoice issued by your organisation that includes your ABN, details of GST component (if no GST, please note this) and bank account details\n• A copy of the original tax-compliant invoice or receipt from the supplier\n• Proof of payment showing your organisation has paid the supplier\n\nPlease upload all documents via the File Upload tab in this request.\n\nKind regards",
  },
  {
    id: "i08",
    category: "Invoicing",
    title: "Invoice & Receipt for Reimbursement (Uploaded)",
    text: "Thanks for submitting the invoice and receipt relating to this request. They have been processed through to our accounts team to arrange the reimbursement. Your organisation can expect to receive the funds in the next few days.\n\nKind regards",
  },
  {
    id: "i09",
    category: "Invoicing",
    title: "Invoice & Receipt for Reimbursement (Emailed from Head Office)",
    text: "We have now received the invoice and receipt relating to this request. They have been processed through to our accounts team to arrange the reimbursement. Your organisation can expect to receive the funds in the next few days.\n\nKind regards",
  },
  {
    id: "i10",
    category: "Invoicing",
    title: "Requesting Proof of Payment / Receipt",
    text: 'Thanks for the invoice. To process reimbursement, we need proof of payment.\n\nACCEPTABLE PROOF (any one of):\n• Original invoice showing zero balance / "paid"\n• Online order confirmation showing payment method\n• Remittance advice from online banking\n• Screenshot of online payment\n• Credit card statement line or EFTPOS receipt\n\nYOUR ACTION:\n• Upload proof of payment via FILES & INVOICES tab\n\nKind regards',
  },
  {
    id: "i11",
    category: "Invoicing",
    title: "Requesting DFV/Org Invoice for Reimbursement",
    text: "Thank you for forwarding the receipt. In order to process the reimbursement, could you please have your accounts team raise an invoice to Zephyr Education Limited to accompany the receipt. As soon as that is received, we will expedite the transfer of funds for you.\n\nPlease let us know if you have any queries.\n\nKind regards",
  },
  {
    id: "i12",
    category: "Invoicing",
    title: "Receipt for Reimbursement without Invoice (Alternate)",
    text: "Thank you for submitting the receipt for [INSERT description]. We now await the invoice from your finance team to accompany it so that we can proceed to organising your reimbursement.\n\nKind regards",
  },
  {
    id: "i13",
    category: "Invoicing",
    title: "Invoice Less Than Approved",
    text: "We have now received the invoice for this request from the supplier. It has been processed through to our accounts team for payment over the next 1-2 business days.\n\nWe note the original approval was for $[AMOUNT] however the invoice totals $[AMOUNT]. The invoice is not itemised to determine what items were not supplied. We will close this request off assuming the remaining items are no longer required.\n\nIf they are invoiced further, please quote this reference number and we will match the invoice to the outstanding items.\n\nKind regards",
  },
  {
    id: "i14",
    category: "Invoicing",
    title: "Invoice More Than Approved (Outside Guidelines)",
    text: "We have received the invoice from the school for this request and note it totals $[AMOUNT], rather than the $[AMOUNT] approved.\n\nA quick review of the invoice, against the request, reveals that rather than [e.g. 2 items each of shorts and shirts, 3 items of each have been purchased]. Zephyr’s guidelines allow for a total of 3 days’ worth of uniforms and, given [1 x formal shirt and 1 x formal pant are included], we can only cover [INSERT items not covered].\n\nCould you please have the school amend the invoice to us to include only the items approved and re-submit it.\n\nIf you have any queries, please do not hesitate to contact us.\n\nKind regards",
  },
  {
    id: "i15",
    category: "Invoicing",
    title: "Invoice More Than Approved (Within Guidelines)",
    text: "We have received the invoice from the school for this request and note it totals $[AMOUNT], rather than the $[AMOUNT] approved.\n\nA quick review of the invoice, against the request, reveals that 2 days’ worth of uniforms were requested and approved. As Zephyr’s guidelines allow for a total of 3 days’ worth of uniforms, we will approve the extra items on this occasion. We would, however, expect that any future purchases be only for the items approved.\n\nIf you have any queries, please do not hesitate to contact us.\n\nKind regards",
  },
  {
    id: "i16",
    category: "Invoicing",
    title: "Unable to Match Invoice from Supplier to Request",
    text: "We've received an invoice from the supplier but it doesn't include a Zephyr reference number, and we can't match it to any approved request.\n\nYOUR ACTION:\n• If you have an approved request, reply with the reference number (e.g. 2025-00xxx)\n• If not, please submit a new request on the website and attach the invoice\n\nKind regards",
  },
  {
    id: "i17",
    category: "Invoicing",
    title: "Supplier Unable to Provide Invoice",
    text: "We are unable to pay the costs without a compliant tax invoice made out to Zephyr Education Limited. If the supplier does not have one, we have a pro forma we can email you to pass on to them to complete. It is an ATO requirement that suppliers provide a tax invoice upon request.\n\nPlease let us know if you require further assistance in this regard.\n\nKind regards",
  },
  {
    id: "i18",
    category: "Invoicing",
    title: "Supplier Only Able to Accept Credit Card Payment",
    text: "This supplier has advised they are only able to accept payment via credit card which, for security reasons, Zephyr Education is unable to facilitate. Is your organisation able to purchase the uniforms and invoice us (with a receipt) for reimbursement? We will return the funds to you within 1-2 business days. If not, please let us know as soon as possible as we will unfortunately be unable to assist and will need to cancel this request.\n\nKind regards",
  },
  {
    id: "i19",
    category: "Invoicing",
    title: "Alternate for WA DFV Services Used to Credit Card Payment",
    text: "It is unfortunately a situation where we are unable to assist. We do our very best to support our organisations however serious security issues with the credit card payments have given us no option but to discontinue this form of payment. We have been working with another supplier in a similar situation however there appears to be no mutually agreeable solution. Most of our client DFV Services are able to pay the suppliers in these circumstances and we do our best to reimburse the funds within 1-2 business days. We sincerely apologise that we cannot help on this occasion.\n\nKind regards",
  },
];

// Hook -- loads templates from DB, seeds if empty.
// `opts.includeDeactivated` defaults to false (shelter/responder use) — the
// admin (TemplatesAdmin) passes true so it can see + reactivate soft-deleted rows.
interface CommentTemplateRow {
  id: string;
  category?: string;
  title?: string;
  text?: string;
  deactivated?: boolean;
}

export function useCommentTemplates(opts?: { includeDeactivated?: boolean }) {
  var includeDeactivated = !!opts?.includeDeactivated;
  var _s = React.useState<CommentTemplateRow[]>([]);
  var templates = _s[0];
  var setTemplates = _s[1];
  var _l = React.useState(true);
  var loading = _l[0];
  var setLoading = _l[1];

  async function loadTemplates() {
    var res = await listCommentTemplates(includeDeactivated);
    if (res.error) {
      setLoading(false);
      return;
    }
    var existing = res.data || [];

    if (existing.length === 0) {
      // First time — insert all seed templates
      await insertCommentTemplates(TEMPLATE_SEED);
      setTemplates(TEMPLATE_SEED);
      setLoading(false);
      return;
    }

    // Find any seed templates missing from the DB and insert them
    var existingIds = existing.map(function (t) {
      return t.id;
    });
    var missing = TEMPLATE_SEED.filter(function (t) {
      return existingIds.indexOf(t.id) === -1;
    });
    if (missing.length > 0) {
      await insertCommentTemplates(missing);
      // Re-fetch after inserting missing templates
      var res2 = await listCommentTemplates(includeDeactivated);
      setTemplates(res2.data || existing);
      setLoading(false);
    } else {
      setTemplates(existing);
      setLoading(false);
    }
  }

  React.useEffect(function () {
    loadTemplates();
  }, []);

  return { templates: templates, loading: loading, reload: loadTemplates };
}
